import React from "react";
import Header from "components/Header";

import {
    Chart as ChartJS,
    BarElement,
    LineElement,
    PointElement,
    ArcElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
} from "chart.js";
import {
    FaUsers,
    FaUserPlus,
    FaBaby,
    FaHeartbeat,
    FaVenusMars,
    FaGraduationCap,
    FaHome,
} from "react-icons/fa";

import { Line, Bar, Doughnut } from "react-chartjs-2";
const demographicKpis = [
    {
        title: "Total Population",
        value: 7.8,
        unit: "Cr",
        change: 1.2,
        trendData: [7.4, 7.5, 7.6, 7.7, 7.8],
        icon: FaUsers,
    },
    {
        title: "Population (30+)",
        value: 3.2,
        unit: "Cr",
        change: 2.1,
        trendData: [2.8, 2.9, 3.0, 3.1, 3.2],
        icon: FaUserPlus,
    },
    {
        title: "Birth Rate",
        value: 17.4,
        unit: "births / 1000 population",
        change: -1.5, // YoY decrease
        trendData: [18.5, 18.1, 17.9, 17.6, 17.4],
        icon: FaBaby,
    },
    {
        title: "Death Rate",
        value: 6.2,
        unit: "/1000 population",
        change: -0.8,
        trendData: [6.8, 6.6, 6.4, 6.3, 6.2],
        icon: FaHeartbeat,
    },
    {
        title: "Sex Ratio at Birth",
        value: 929,
        change: 0.6,
        trendData: [920, 922, 924, 927, 929],
        icon: FaVenusMars,
        isRatio: true,
        ratioSuffix: "girls per 1000 boys",
    },
    {
        title: "Female Literacy",
        value: 71,
        unit: "%",
        change: 1.8,
        trendData: [67, 68, 69, 70, 71],
        icon: FaGraduationCap,
    },
];

const ageGenderPopulation = {
    labels: ["0–14", "15–29", "30–44", "45–59", "60+"],
    male: [18, 21, 24, 16, 9],
    female: [17, 20, 23, 15, 10],
};
const urbanRuralSplit = {
    urban: 38,
    rural: 62,
};
const population30Trend = {
    labels: ["2011", "2014", "2017", "2020", "2023","2024","2025"],
    values: [28.7, 29.2, 30.1, 31.2, 32.0, 34.6, 36.0],
};
const planningSignals = [
    {
        title: "Dependency Ratio",
        value: "54%",
        note: "High dependent population per 100 working-age adults",
    },
    {
        title: "Ageing Index",
        value: "68",
        note: "68 elderly per 100 children (0–14 years)",
    },
    {
        title: "Literacy Gender Gap",
        value: "13%",
        note: "Female literacy lags behind male literacy",
    },
    {
        title: "Urbanisation Level",
        value: "38%",
        note: "Growing demand on urban health infrastructure",
    },
];

const PlanningSignalCard = ({ title, value, note }) => (
    <div className="bg-white rounded-xl shadow-sm p-4">
        <p className="text-xs text-gray-500">{title}</p>

        <h3 className="text-2xl font-semibold text-gray-900 mt-1">
            {value}
        </h3>

        <p className="text-xs text-gray-400 mt-1 leading-snug">
            {note}
        </p>
    </div>
);
const DemographicInterpretation = () => (
    <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4">
        <p className="text-sm text-indigo-800">
            <strong>Planning Implication:</strong>
            The population profile indicates a dual burden of youth services and
            ageing-related healthcare needs. Urbanisation and literacy gaps suggest
            the need for targeted urban health strengthening and female education
            interventions.
        </p>
    </div>
);

const Population30Trend = () => (
    <div className="bg-white rounded-xl shadow-sm p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-3">
            Population Aged 30+ (% of Total)
        </h3>

        <div className="">
            <Line
                data={{
                    labels: population30Trend.labels,
                    datasets: [
                        {
                            label: "Population 30+",
                            data: population30Trend.values,
                            borderColor: "#6366f1",
                            backgroundColor: "rgba(99,102,241,0.15)",
                            tension: 0.4,
                            fill: true,
                            pointRadius: 3,
                        },
                    ],
                }}
                options={{
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            callbacks: {
                                label: (ctx) => `${ctx.parsed.y}%`,
                            },
                        },
                        datalabels: false
                    },
                    scales: {
                        y: {
                            ticks: { callback: (v) => `${v}%` },
                            grid: { color: "#f1f5f9" },
                        },
                        x: { grid: { display: false } },
                    },
                }}
            />
        </div>
    </div>
);

const UrbanRuralSplitBar = () => (
    <div className="bg-white rounded-xl shadow-sm p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-3">
            Urban–Rural Population Distribution (%)
        </h3>

        <div className="">
            <Bar
                data={{
                    labels: ["Population"],
                    datasets: [
                        {
                            label: "Urban",
                            data: [urbanRuralSplit.urban],
                            backgroundColor: "#34d399",
                        },
                        {
                            label: "Rural",
                            data: [urbanRuralSplit.rural],
                            backgroundColor: "#94a3b8",
                        },
                    ],
                }}
                options={{
                    indexAxis: "y",
                    plugins: {
                        legend: { position: "bottom" },
                        tooltip: {
                            callbacks: {
                                label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.x}%`,
                            },
                        },
                        datalabels: false
                    },
                    scales: {
                        x: {
                            stacked: true,
                            max: 100,
                            ticks: { callback: (v) => `${v}%` },
                            grid: { color: "#f1f5f9" },
                        },
                        y: {
                            stacked: true,
                            grid: { display: false },
                        },
                    },
                }}
            />
        </div>
    </div>
);

const AgeGenderPopulationChart = () => (
    <div className="bg-white rounded-xl shadow-sm p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-3">
            Population Distribution by Age & Gender (%)
        </h3>

        <div className="">
            <Bar
                data={{
                    labels: ageGenderPopulation.labels,
                    datasets: [
                        {
                            label: "Male",
                            data: ageGenderPopulation.male,
                            backgroundColor: "#60a5fa",
                            barThickness: 26,
                        },
                        {
                            label: "Female",
                            data: ageGenderPopulation.female,
                            backgroundColor: "#f472b6",
                            barThickness: 26,
                        },
                    ],
                }}
                options={{
                    plugins: {
                        legend: { position: "bottom" },
                        tooltip: {
                            callbacks: {
                                label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y}%`,
                            },
                        },
                        datalabels: false
                    },
                    scales: {
                        y: {
                            stacked: true,
                            ticks: { callback: (v) => `${v}%` },
                            grid: { color: "#f1f5f9" },
                        },
                        x: {
                            stacked: true,
                            grid: { display: false },
                        },
                    },
                }}
            />
        </div>
    </div>
);

const MetricKpiCard = ({
    title,
    value,
    unit,
    change,
    trendData,
    icon: Icon,
    isRatio = false,
    ratioSuffix = "",
}) => {
    const isNegative = change < 0;

    return (
        <div className="bg-white rounded-xl shadow-sm p-3 overflow-hidden justify-center">
            <div className="flex items-center gap-3">
                {/* Icon (shrink-safe) */}
                <div className="shrink-0 h-9 w-9 flex items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                    <Icon size={16} />
                </div>

                {/* Content */}
                <div className="min-w-0 flex-1">
                    {/* Title */}
                    <p className="text-xs text-gray-500 truncate">
                        {title}
                    </p>

                    {/* Value */}
                    <h3 className="text-lg font-semibold text-gray-900 leading-tight">
                        {isRatio ? (
                            <>
                                {value}
                                <span className="text-gray-500 font-medium"> : 1000</span>
                            </>
                        ) : (
                            <>
                                {value}
                                {unit && (
                                    <span className="ml-1 text-sm font-medium text-gray-500">
                                        {unit}
                                    </span>
                                )}
                            </>
                        )}
                    </h3>

                    {/* Ratio helper */}
                    {isRatio && (
                        <p className="text-[11px] text-gray-400 leading-tight">
                            {ratioSuffix}
                        </p>
                    )}

                    {/* Change */}
                    <p
                        className={`text-[11px] mt-0.5 ${isNegative ? "text-rose-600" : "text-green-600"
                            }`}
                    >
                        {isNegative ? "▼" : "▲"} {Math.abs(change)}%
                        <span className="text-gray-400 ml-1">
                            vs previous period
                        </span>
                    </p>

                    {/* Mini trend */}
                    {/* {trendData?.length > 0 && (
                        <div className="mt-1 h-8">
                            <Line
                                data={{
                                    labels: trendData.map((_, i) => i),
                                    datasets: [
                                        {
                                            data: trendData,
                                            borderColor: isNegative
                                                ? "#f43f5e"
                                                : "#22c55e",
                                            backgroundColor: isNegative
                                                ? "rgba(244,63,94,0.12)"
                                                : "rgba(34,197,94,0.12)",
                                            fill: true,
                                            tension: 0.4,
                                            pointRadius: 0,
                                            borderWidth: 1.5,
                                        },
                                    ],
                                }}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                        legend: { display: false },
                                        tooltip: { enabled: false },
                                    },
                                    scales: {
                                        x: { display: false },
                                        y: { display: false },
                                    },
                                }}
                            />
                        </div>
                    )} */}
                </div>
            </div>
        </div>
    );
};




export default function DemographicPopulationDashboard() {
    const handleMenuClick = () => {
        console.log("menu clicked (header)");
    };

    return (
        <>
            <div className="min-h-screen bg-gray-50">
                <Header onMenuClick={handleMenuClick} dashboardName="Demographic Population Dashboard" />
                <div className="p-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-2">
                        {demographicKpis.map((kpi, idx) => (
                            <MetricKpiCard key={idx} {...kpi} />
                        ))}
                    </div>

                    <section className="space-y-6">
                        <h2 className="text-lg font-semibold text-gray-800 mt-2">
                            Population Structure & Demographic Dynamics
                        </h2>

                        <div className="grid grid-cols-12 gap-6">
                            <div className="col-span-12 lg:col-span-4">
                                <AgeGenderPopulationChart />
                            </div>

                            <div className="col-span-12 lg:col-span-4 space-y-6">
                                <UrbanRuralSplitBar />

                            </div>
                            <div className="col-span-12 lg:col-span-4 space-y-6">
                                <Population30Trend />
                            </div>
                        </div>
                    </section>
                    <section className="space-y-4">
                        <h2 className="text-lg font-semibold text-gray-800 mt-2">
                            Demographic Implications & Planning Signals
                        </h2>

                        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                            {planningSignals.map((item, idx) => (
                                <PlanningSignalCard key={idx} {...item} />
                            ))}
                        </div>

                        <DemographicInterpretation />
                    </section>

                </div>



            </div>

        </>
    );
}